import { FilterService, useAuthStore } from '@supply-mover-2-0/core';
import React, { useEffect, useState } from 'react';
import { useListBuilderContextSafe } from '../../list-builder/components/core/ListBuilderProvider';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { FormField } from '../forms/FormField';
import type { MultiSelectOption } from '../forms/MultiSelect';
import { MultiSelect } from '../forms/MultiSelect';
import type { FilterConfig, FilterOption } from './BaseFilter';

interface BaseMultiSelectProps {
	filterType: string;
	config: FilterConfig;
	selectedValues: FilterOption[];
	onValuesChange: (values: FilterOption[]) => void;
	disabled?: boolean;
	isModalOpen?: boolean;
	lockedValues?: FilterOption[];
	/** When set, restricts the dropdown to only these values (skip API fetch) */
	boundedValues?: FilterOption[];
	size?: 'xs' | 'sm' | 'md' | 'lg';
	layout?: 'sidebar' | 'tray' | 'inline';
}

/**
 * BaseMultiSelect Component
 *
 * A filter component that wraps the MultiSelect component and provides
 * integration with the filter system, including dynamic loading of options
 * from FilterService.
 */
export const BaseMultiSelect: React.FC<BaseMultiSelectProps> = ({
	filterType,
	config,
	selectedValues,
	onValuesChange,
	disabled = false,
	isModalOpen = false,
	lockedValues = [],
	boundedValues,
	size = 'md',
	layout = 'inline',
}) => {
	const colors = useThemeColors();
	const { t } = useTranslation();

	// Get current user for ME option handling (Jira-style: move current user to top)
	const user = useAuthStore((state) => state.user);
	const currentUserId = user?.instanceUserId ?? user?.id;

	// Use the safe hook to check if we're in a ListBuilder context
	const listBuilderContext = useListBuilderContextSafe();

	// Filter out ME from initial options to prevent flicker (current user will be added after API load)
	const [options, setOptions] = useState<FilterOption[]>(() =>
		(config.options || []).filter((opt) => opt.id !== 'ME'),
	);
	const [loading, setLoading] = useState(false);
	const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
		null,
	);
	const [hasLoadedInitial, setHasLoadedInitial] = useState(false);

	// Extract a display badge from FilterOption.data (e.g., geography type)
	const getLocationBadge = (data: unknown): string | undefined => {
		if (data == null || typeof data !== 'object') {
			return undefined;
		}
		const record = data as Record<string, unknown>;
		const type = record.type;
		if (type == null) {
			return undefined;
		}
		const badgeMap: Record<string, string> = {
			city: 'City',
			state: 'State',
			county: 'County',
			zip: 'ZIP',
			address: 'Address',
		};
		return badgeMap[String(type)];
	};

	// Convert FilterOption to MultiSelectOption
	const convertToMultiSelectOption = (
		option: FilterOption,
		isLocked: boolean = false,
	): MultiSelectOption => {
		// Safely handle undefined or null IDs
		if (option.id === undefined || option.id === null) {
			// Warning: FilterOption has undefined or null ID
			const nullIdBadge = getLocationBadge(option.data);
			return {
				value: '',
				label: t(option.label || 'Unknown'),
				count: option.count,
				subtitle: option.subtitle ? t(option.subtitle) : undefined,
				isFixed: isLocked,
				badge: nullIdBadge != null ? t(nullIdBadge) : undefined,
			};
		}

		// Safely handle undefined or null labels
		const label = option.label != null ? t(option.label) : t('Unknown');
		const badge = getLocationBadge(option.data);

		return {
			value: String(option.id), // Use String() instead of toString() for safer conversion
			label,
			count: option.count,
			subtitle: option.subtitle ? t(option.subtitle) : undefined,
			isFixed: isLocked,
			badge: badge != null ? t(badge) : undefined,
		};
	};

	// Convert MultiSelectOption back to FilterOption
	const convertToFilterOption = (option: MultiSelectOption): FilterOption => {
		const originalOption = [
			...options,
			...selectedValues,
			...lockedValues,
		].find(
			(o) =>
				o.id !== undefined && o.id !== null && String(o.id) === option.value,
		);
		return (
			originalOption || {
				id: option.value,
				label: option.label,
				count: option.count,
				subtitle: option.subtitle,
			}
		);
	};

	// Load options from FilterService
	const loadOptions = async (searchTerm: string = '') => {
		if (!config.apiEndpoint) {
			return;
		}

		setLoading(true);
		try {
			// Build filter context for dependent filters
			const contextForFilter: Record<string, any> = {};

			// For subcategory filter, include selected categories and manufacturers as context
			if (filterType === 'subcategory' && listBuilderContext) {
				// Use ListBuilder's filter state
				const categoryValues =
					listBuilderContext.state.filterState.category || [];
				const manufacturerValues =
					listBuilderContext.state.filterState.manufacturer || [];

				if (categoryValues.length > 0) {
					contextForFilter.category = categoryValues.map((v) => String(v.id));
				}
				if (manufacturerValues.length > 0) {
					contextForFilter.manufacturer = manufacturerValues.map((v) =>
						String(v.id),
					);
				}
			}

			// For manufacturer filter, include selected categories and subcategories as context
			if (filterType === 'manufacturer' && listBuilderContext) {
				// Use ListBuilder's filter state
				const categoryValues =
					listBuilderContext.state.filterState.category || [];
				const subcategoryValues =
					listBuilderContext.state.filterState.subcategory || [];

				if (categoryValues.length > 0) {
					contextForFilter.category = categoryValues.map((v) => String(v.id));
				}
				if (subcategoryValues.length > 0) {
					contextForFilter.subcategory = subcategoryValues.map((v) =>
						String(v.id),
					);
				}
			}

			// For category filter, include selected manufacturers as context
			if (filterType === 'category' && listBuilderContext) {
				// Use ListBuilder's filter state
				const manufacturerValues =
					listBuilderContext.state.filterState.manufacturer || [];

				if (manufacturerValues.length > 0) {
					contextForFilter.manufacturer = manufacturerValues.map((v) =>
						String(v.id),
					);
				}
			}

			const results = await FilterService.getFilterOptions(
				filterType,
				searchTerm,
				false, // isConfigMode
				Object.keys(contextForFilter).length > 0 ? contextForFilter : undefined,
			);

			// Filter out options with invalid IDs or labels
			const validResults = results.filter((option) => {
				if (option.id === undefined || option.id === null) {
					// Warning: Filtering out option with invalid ID
					return false;
				}
				if (option.label === undefined || option.label === null) {
					// Warning: Filtering out option with invalid label
					return false;
				}
				return true;
			});

			// Preserve static options from config and merge with dynamic options
			let staticOptions = config.options || [];
			let finalResults = [...validResults];

			// Handle ME option: Move current user to top of list (Jira-style)
			const hasMeOption = staticOptions.some((opt) => opt.id === 'ME');
			if (hasMeOption && currentUserId != null) {
				// Find current user in API results and move to top
				const currentUserIndex = finalResults.findIndex(
					(opt) => opt.id === currentUserId || opt.id === String(currentUserId),
				);

				if (currentUserIndex >= 0) {
					const [currentUser] = finalResults.splice(currentUserIndex, 1);
					finalResults = [currentUser, ...finalResults];
				}

				// Remove ME from static options since we've moved actual user to top
				staticOptions = staticOptions.filter((opt) => opt.id !== 'ME');
			}

			const mergedOptions = [...staticOptions, ...finalResults];
			setOptions(mergedOptions);
		} catch (error) {
			// // Error: Failed to load options for filter
			// Keep static options even if API fails
			setOptions(config.options || []);
		} finally {
			setLoading(false);
		}
	};

	// Handle search with debouncing
	const handleSearch = (searchTerm: string) => {
		if (!config.searchable || !config.apiEndpoint) {
			return;
		}

		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		const timeout = setTimeout(() => {
			loadOptions(searchTerm);
		}, 300);

		setSearchTimeout(timeout);
	};

	// Load initial options for filters that DON'T have loadOnFocus
	// Filters with loadOnFocus will load when menu opens
	// Skip API loading entirely when bounded (bounded values are the only allowed options)
	useEffect(() => {
		if (
			boundedValues == null &&
			!config.loadOnFocus &&
			config.apiEndpoint &&
			options.length === 0 &&
			!hasLoadedInitial
		) {
			setHasLoadedInitial(true);
			loadOptions();
		}
	}, []);

	// Load full data for selected values that only have IDs as labels
	useEffect(() => {
		if (config.apiEndpoint && selectedValues.length > 0) {
			// Check if any selected values need their labels loaded
			// A value needs loading if its label is the same as its ID (temporary label)
			// Also check for numeric IDs that might have been stringified
			const needsLoading = selectedValues.some((v) => {
				// Check if label is missing or undefined
				if (v.label === undefined || v.label === null || v.label === '') {
					return true;
				}

				const idStr = String(v.id);
				const labelStr = String(v.label);
				// Check if label is same as ID (temporary label)
				// or if label looks like an ID (numeric or UUID-like)
				return (
					labelStr === idStr ||
					labelStr === v.id ||
					v.label === v.id ||
					// Also check if label is numeric (likely an ID)
					(!isNaN(Number(labelStr)) && labelStr === idStr)
				);
			});

			if (needsLoading) {
				// Load these specific items
				loadSelectedOptions();
			}
		}
	}, [selectedValues.length, config.apiEndpoint]); // Only depend on length to avoid infinite loops

	// Function to load full data for selected options
	const loadSelectedOptions = async () => {
		if (!config.apiEndpoint || selectedValues.length === 0) {
			return;
		}

		try {
			// Get IDs of values that need their labels loaded
			const idsNeedingLabels = selectedValues
				.filter((v) => {
					// Check if label is missing or undefined
					if (v.label === undefined || v.label === null || v.label === '') {
						return true;
					}

					const idStr = String(v.id);
					const labelStr = String(v.label);
					// Check if label looks like it needs loading
					return (
						labelStr === idStr ||
						labelStr === v.id ||
						v.label === v.id ||
						(!isNaN(Number(labelStr)) && labelStr === idStr)
					);
				})
				.map((v) => v.id);

			if (idsNeedingLabels.length === 0) {
				return;
			}

			// Try different approaches to load the data
			// First try with comma-separated IDs (works for some endpoints)
			const searchTerm = idsNeedingLabels.join(',');

			let results = await FilterService.getFilterOptions(
				filterType,
				searchTerm,
				false,
				undefined,
			);

			// If no results, try loading each ID individually
			if (results.length === 0 && idsNeedingLabels.length <= 10) {
				const individualResults = await Promise.all(
					idsNeedingLabels.map(
						(id) =>
							FilterService.getFilterOptions(
								filterType,
								String(id),
								false,
								undefined,
							).catch(() => []), // Silently fail individual requests
					),
				);
				// Flatten and deduplicate results
				const allResults = individualResults.flat();
				const uniqueResults = new Map<string, any>();
				allResults.forEach((r) => {
					if (r.id != null) {
						uniqueResults.set(String(r.id), r);
					}
				});
				results = Array.from(uniqueResults.values());
			}

			// Create a map of loaded options
			const loadedOptionsMap = new Map(
				results.map((opt) => [String(opt.id), opt]),
			);

			// Update selected values with proper labels
			const updatedValues = selectedValues.map((selected) => {
				const loadedOption = loadedOptionsMap.get(String(selected.id));
				if (loadedOption) {
					// Check if this value needs its label updated
					if (
						selected.label === undefined ||
						selected.label === null ||
						selected.label === ''
					) {
						// Replace missing label with actual label
						return {
							...selected,
							label: loadedOption.label,
							subtitle: loadedOption.subtitle,
							data: loadedOption.data,
						};
					}

					const idStr = String(selected.id);
					const labelStr = String(selected.label);
					// Check if label is same as ID (temporary label)
					if (
						labelStr === idStr ||
						labelStr === selected.id ||
						selected.label === selected.id ||
						(!isNaN(Number(labelStr)) && labelStr === idStr)
					) {
						// Replace temporary label with actual label
						return {
							...selected,
							label: loadedOption.label,
							subtitle: loadedOption.subtitle,
							data: loadedOption.data,
						};
					}
				}
				return selected;
			});

			// Only update if values actually changed
			const hasChanges = updatedValues.some(
				(updated, index) => updated.label !== selectedValues[index].label,
			);

			if (hasChanges) {
				onValuesChange(updatedValues);

				// Also update options to include the loaded values
				setOptions((prevOptions) => {
					const optionMap = new Map(
						prevOptions.map((opt) => [String(opt.id), opt]),
					);
					results.forEach((opt) => {
						if (!optionMap.has(String(opt.id))) {
							optionMap.set(String(opt.id), opt);
						}
					});
					return Array.from(optionMap.values());
				});
			}
		} catch (error) {
			// Silently fail - the user can still see IDs which is better than nothing
			console.debug('Failed to load labels for selected filter values:', error);
		}
	};

	// Reload subcategory options when category or manufacturer filter changes
	useEffect(() => {
		if (
			filterType === 'subcategory' &&
			config.apiEndpoint &&
			listBuilderContext
		) {
			loadOptions();
		}
	}, [
		listBuilderContext?.state.filterState.category,
		listBuilderContext?.state.filterState.manufacturer,
	]);

	// Reload manufacturer options when category or subcategory filter changes
	useEffect(() => {
		if (
			filterType === 'manufacturer' &&
			config.apiEndpoint &&
			listBuilderContext
		) {
			loadOptions();
		}
	}, [
		listBuilderContext?.state.filterState.category,
		listBuilderContext?.state.filterState.subcategory,
	]);

	// Reload category options when manufacturer filter changes
	useEffect(() => {
		if (filterType === 'category' && config.apiEndpoint && listBuilderContext) {
			loadOptions();
		}
	}, [listBuilderContext?.state.filterState.manufacturer]);

	// Cleanup timeout on unmount
	useEffect(
		() => () => {
			if (searchTimeout) {
				clearTimeout(searchTimeout);
			}
		},
		[searchTimeout],
	);

	// Remove locked values from selectedValues to avoid duplicates
	const unlockedSelectedValues = selectedValues.filter(
		(selected) => !lockedValues.some((locked) => locked.id === selected.id),
	);
	// Combine deduplicated values
	const allSelectedValues = [...unlockedSelectedValues, ...lockedValues];

	// When bounded, use only bounded values as the dropdown options (skip API-loaded options)
	const effectiveOptions = boundedValues != null ? boundedValues : options;

	// Convert options to MultiSelect format, filtering out invalid ones
	const multiSelectOptions = effectiveOptions
		.filter(
			(opt) =>
				opt.id !== undefined &&
				opt.id !== null &&
				opt.label !== undefined &&
				opt.label !== null,
		)
		.map((opt) => convertToMultiSelectOption(opt));

	const multiSelectValues = allSelectedValues
		.filter((opt) => opt.id !== undefined && opt.id !== null)
		.map((opt) => {
			// Check if this option is in the locked values
			const isLocked = lockedValues.some((locked) => locked.id === opt.id);
			return convertToMultiSelectOption(opt, isLocked);
		});

	// Handle value changes
	const handleChange = (newValues: MultiSelectOption[]) => {
		// Filter out locked values and convert back to FilterOptions
		const unlockedValues = newValues.filter((v) => !v.isFixed);
		const filterOptions = unlockedValues.map(convertToFilterOption);
		onValuesChange(filterOptions);
	};

	// Handle menu open - load options if configured to load on focus
	const handleMenuOpen = () => {
		// Skip API loading when bounded (bounded values are the only allowed options)
		if (boundedValues != null) {
			return;
		}

		// Only load if:
		// 1. Filter is configured to load on focus (not on mount)
		// 2. Has an API endpoint
		// 3. Hasn't loaded initial options yet
		// 4. Not currently loading
		if (
			config.loadOnFocus &&
			config.apiEndpoint &&
			!hasLoadedInitial &&
			!loading
		) {
			// Mark as loading started BEFORE calling loadOptions to prevent duplicate calls
			setHasLoadedInitial(true);
			// Load options immediately (async but we don't wait)
			loadOptions();
		}
	};

	return (
		<FormField
			name={filterType}
			label={t(config.label)}
			required={config.required}
			size={size}
		>
			<MultiSelect
				name={config.queryParamName || filterType}
				value={multiSelectValues}
				options={multiSelectOptions}
				onChange={handleChange}
				placeholder={config.placeholder ? t(config.placeholder) : undefined}
				isDisabled={disabled || isModalOpen}
				isLoading={boundedValues != null ? false : loading}
				isSearchable={boundedValues != null ? false : config.searchable}
				onInputChange={
					boundedValues == null && config.searchable ? handleSearch : undefined
				}
				onMenuOpen={handleMenuOpen}
				maxMenuHeight={250}
				size={size}
				filterOption={
					config.searchable === true && config.apiEndpoint != null
						? () => true
						: undefined
				}
				menuMinWidth={config.menuMinWidth}
			/>
		</FormField>
	);
};
